import { LockClosedIcon, TagIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { usePermissions } from 'hooks/usePermissions'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { getLoanSystemTags, getLoanTags, getLoanTagSuggestions, updateLoanTags } from 'services'
import { confirm } from 'utils'
import { setLoanNumber } from 'utils/setLoanNumber'

// Protected system tags (e.g. Foreclosure/Bankruptcy/REO) are removable only by a user holding
// `Loan.Tags.SystemTag.Remove`; for everyone else the backend puts them back.
// Cache the list module-wide so the many grid-row instances share a single fetch.
let systemTagsCache: string[] | null = null
let systemTagsPromise: Promise<string[]> | null = null
const loadSystemTags = (): Promise<string[]> => {
  if (systemTagsCache) return Promise.resolve(systemTagsCache)
  if (!systemTagsPromise) {
    systemTagsPromise = getLoanSystemTags()
      .then((t) => (systemTagsCache = Array.isArray(t) ? t : []))
      .catch(() => (systemTagsCache = []))
  }
  return systemTagsPromise
}

// Free-form tags for a loan (origination + servicing), with autocomplete of tags used elsewhere.
export const LoanTags = ({
  loanNumber: loanNumberProp,
  readOnly = false,
  compact = false,
  initialTags,
}: {
  loanNumber?: number | string
  readOnly?: boolean
  // Tighter layout for rendering inside a grid cell.
  compact?: boolean
  // Seed tags (e.g. from the pipeline payload) to avoid an initial per-row fetch.
  initialTags?: string[]
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const { hasPermission } = usePermissions()
  // Resolve the loan explicitly so tags are always scoped to the right loan.
  const loanNumber =
    loanNumberProp && Number(loanNumberProp) > 0 ? Number(loanNumberProp) : Number(setLoanNumber()) || 0
  const [tags, setTags] = useState<string[]>(Array.isArray(initialTags) ? initialTags : [])
  const [systemTags, setSystemTags] = useState<string[]>(systemTagsCache || [])
  const [input, setInput] = useState('')
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  // Compact (grid) overflow: how many chips fit on one line, and whether the "+N" popover is open.
  const rowRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(tags.length)
  const [showOverflow, setShowOverflow] = useState(false)

  useEffect(() => {
    // If tags were seeded from the payload, trust them and skip the fetch.
    if (!loanNumber || Array.isArray(initialTags)) return
    let cancelled = false
    getLoanTags(loanNumber)
      .then((t) => !cancelled && setTags(Array.isArray(t) ? t : []))
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [loanNumber])

  useEffect(() => {
    let cancelled = false
    loadSystemTags().then((t) => !cancelled && setSystemTags(t))
    return () => {
      cancelled = true
    }
  }, [])
  const isSystemTag = (t: string) => systemTags.some((s) => s.toLowerCase() === t.toLowerCase())
  // Clearing a legal state is a deliberate act, so the X on a system tag appears only for the
  // roles an admin has granted it to. Everyone else sees the padlock, as before.
  const canRemoveSystemTag = !!hasPermission('Loan.Tags.SystemTag.Remove')
  const canRemove = (t: string) => !readOnly && (!isSystemTag(t) || canRemoveSystemTag)

  /**
   * System tags lead the row. In the grid the chip row is clamped to one line and everything
   * past it collapses into a "+N", so a loan carrying Foreclosure behind a few ordinary tags
   * hid the one tag that says the loan is in distress. Order within each group is untouched,
   * and this only affects display: the saved array keeps the order it had.
   */
  const orderedTags = useMemo(() => {
    const system: string[] = []
    const rest: string[] = []
    tags.forEach((t) => (isSystemTag(t) ? system : rest).push(t))
    return [...system, ...rest]
  }, [tags, systemTags])


  // Keep local tags in sync when the seeded prop changes (e.g. a grid row is reused for a
  // different loan, or the pipeline payload refreshes). Keyed by content so it only fires on a
  // real change — not on every render — so it never clobbers an in-flight local edit.
  const initKey = Array.isArray(initialTags) ? JSON.stringify(initialTags) : null
  useEffect(() => {
    if (initKey != null) setTags(JSON.parse(initKey))
  }, [initKey])

  // Debounced suggestion lookup while the add-box is open.
  useEffect(() => {
    if (!open) return
    const id = setTimeout(() => {
      getLoanTagSuggestions(input)
        .then((s) => setSuggestions(Array.isArray(s) ? s : []))
        .catch(() => {})
    }, 200)
    return () => clearTimeout(id)
  }, [input, open])

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false)
        setShowOverflow(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  // Compact mode only: measure how many chips fit on one line (via an off-screen mirror that
  // always holds every chip), reserving room for the "+N" chip and the add-tag input. Re-runs
  // when the tags or the column width change.
  useLayoutEffect(() => {
    if (!compact) {
      setVisibleCount(tags.length)
      return
    }
    const compute = () => {
      const row = rowRef.current
      const mirror = measureRef.current
      if (!row || !mirror) return
      const avail = row.clientWidth
      const chips = Array.from(mirror.querySelectorAll<HTMLElement>('[data-chip]'))
      const GAP = 4 // gap-1
      const PLUS = 30 // room for the "+N" chip
      const INPUT = readOnly ? 0 : 60 // room for the "+ tag" input on the same line
      let used = 0
      let count = 0
      for (let i = 0; i < chips.length; i++) {
        const w = chips[i].offsetWidth + (i > 0 ? GAP : 0)
        const moreAfter = i < chips.length - 1
        const reserve = (moreAfter ? PLUS + GAP : 0) + INPUT
        if (used + w + reserve <= avail) {
          used += w
          count++
        } else break
      }
      // Always show at least one chip when tags exist, so a row is never blank.
      setVisibleCount(tags.length ? Math.max(count, 1) : 0)
    }
    compute()
    const ro = new ResizeObserver(compute)
    if (rowRef.current) ro.observe(rowRef.current)
    return () => ro.disconnect()
  }, [orderedTags, compact, readOnly, canRemoveSystemTag])

  const persist = (next: string[]) => {
    if (!loanNumber) return
    setTags(next)
    setSaving(true)
    updateLoanTags(loanNumber, next)
      .then((saved) => Array.isArray(saved) && setTags(saved))
      .catch(() => {})
      .finally(() => setSaving(false))
  }

  const addTag = (raw: string) => {
    const t = raw.trim()
    setInput('')
    if (!t) return
    if (tags.some((x) => x.toLowerCase() === t.toLowerCase())) return
    persist([...tags, t])
  }

  const removeTag = async (t: string) => {
    // A system tag needs the permission (the backend re-adds it otherwise) and, since it says the
    // loan is in a legal process, one confirmation before it goes.
    if (isSystemTag(t)) {
      if (!canRemoveSystemTag) return
      // The content goes through a variable rather than straight into the call: production Babel
      // crashes generating a JSX literal passed to an awaited call (`Cannot read properties of
      // undefined (reading 'start')`), which neither eslint nor a typecheck sees.
      const content = (
        <div className="text-left text-[14px]">
          Remove the <span className="font-semibold">{t}</span> tag from loan {loanNumber}?
          <div className="mt-2 text-[13px] text-gray-500">
            This clears a system tag that records the loan's legal state. The removal is recorded in
            the loan's audit trail.
          </div>
        </div>
      )
      if (!(await confirm(content))) return
    }
    persist(tags.filter((x) => x !== t))
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addTag(input)
    } else if (e.key === 'Backspace' && !input && tags.length) {
      removeTag(tags[tags.length - 1])
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  const filteredSuggestions = suggestions.filter((s) => !tags.some((t) => t.toLowerCase() === s.toLowerCase()))

  const chipClass = (small: boolean, system = false) =>
    `inline-flex items-center gap-1 rounded-full font-medium ${
      system ? 'bg-amber-100 text-amber-800' : 'bg-shade-blue/10 text-shade-blue'
    } ${small ? 'py-0 pl-2 pr-0.5 text-[11px]' : 'py-0.5 pl-2.5 pr-1 text-[12px]'}`

  const chip = (t: string, small: boolean) => {
    const system = isSystemTag(t)
    const removable = canRemove(t)
    return (
      <span
        key={t}
        className={chipClass(small, system)}
        title={system ? `${t} (system tag${removable ? '' : ', cannot be removed'})` : undefined}
      >
        <span className="max-w-[140px] truncate">{t}</span>
        {removable ? (
          <button
            type="button"
            className={`rounded-full p-0.5 ${
              system
                ? 'text-amber-700/70 hover:bg-amber-200 hover:text-amber-900'
                : 'text-shade-blue/70 hover:bg-shade-blue/20 hover:text-shade-blue'
            }`}
            onClick={() => removeTag(t)}
            aria-label={`Remove ${t}`}
          >
            <XMarkIcon className="h-3 w-3" />
          </button>
        ) : (
          system && <LockClosedIcon className="mr-1 h-3 w-3 opacity-60" aria-label="System tag" />
        )}
      </span>
    )
  }

  const addBox = !readOnly && (
    <div className="relative shrink-0">
      <input
        type="text"
        value={input}
        placeholder={compact ? '+ tag' : '+ Add tag'}
        className={`rounded-full border border-dashed border-slate-300 bg-transparent text-gray-700 placeholder:text-gray-400 focus:border-solid focus:border-shade-blue focus:outline-none ${
          compact ? 'w-14 px-2 py-0 text-[11px] focus:w-24' : 'w-24 px-2.5 py-0.5 text-[12px] focus:w-32'
        }`}
        onChange={(e) => {
          setInput(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {open && filteredSuggestions.length > 0 && (
        <div className="absolute left-0 z-[70] mt-1 max-h-48 w-44 overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg">
          {filteredSuggestions.map((s) => (
            <div
              key={s}
              className="cursor-pointer px-3 py-1.5 text-[13px] text-gray-700 hover:bg-gray-100"
              onMouseDown={(e) => {
                e.preventDefault()
                addTag(s)
              }}
            >
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  )

  // Full (non-grid) layout: show every tag, wrapping freely.
  if (!compact) {
    return (
      <div ref={containerRef} className="flex flex-wrap items-center gap-1.5">
        <TagIcon className="h-4 w-4 text-gray-400" />
        {orderedTags.map((t) => chip(t, false))}
        {tags.length === 0 && readOnly && <span className="text-[12px] text-gray-400">No tags</span>}
        {addBox}
        {saving && <span className="text-[11px] text-gray-400">saving…</span>}
      </div>
    )
  }

  // Compact (grid) layout: single line, clamped to what fits, with a "+N" overflow chip whose
  // popover lists every tag. Keeps row height uniform while making all tags reachable.
  const hiddenTags = orderedTags.slice(visibleCount)
  return (
    <div ref={containerRef} className="relative w-[220px]">
      <div ref={rowRef} className="flex w-full items-center gap-1 overflow-hidden">
        {orderedTags.slice(0, visibleCount).map((t) => chip(t, true))}
        {hiddenTags.length > 0 && (
          <button
            type="button"
            title={hiddenTags.join(', ')}
            className={`${chipClass(true)} shrink-0 cursor-pointer pr-2 hover:bg-shade-blue/20`}
            onClick={() => setShowOverflow((v) => !v)}
          >
            +{hiddenTags.length}
          </button>
        )}
        {tags.length === 0 && readOnly && <span className="text-[11px] text-gray-400">No tags</span>}
        {addBox}
      </div>

      {/* Off-screen mirror holding every chip, used only to measure widths. */}
      <div
        ref={measureRef}
        aria-hidden
        className="pointer-events-none invisible absolute left-0 top-0 flex gap-1 whitespace-nowrap"
      >
        {orderedTags.map((t) => {
          const system = isSystemTag(t)
          return (
            <span key={t} data-chip className={chipClass(true, system)}>
              <span className="max-w-[140px] truncate">{t}</span>
              {canRemove(t) ? (
                <XMarkIcon className="h-3 w-3" />
              ) : (
                system && <LockClosedIcon className="mr-1 h-3 w-3" />
              )}
            </span>
          )
        })}
      </div>

      {/* "+N" popover: the complete tag list, with remove controls when editable. */}
      {showOverflow && (
        <div className="absolute left-0 top-full z-[70] mt-1 flex max-h-48 w-56 flex-wrap gap-1 overflow-auto rounded-md border border-slate-200 bg-white p-2 shadow-lg">
          {tags.map((t) => chip(t, true))}
        </div>
      )}
      {saving && <span className="ml-1 text-[11px] text-gray-400">saving…</span>}
    </div>
  )
}
